import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarClock, Users, GraduationCap, TrendingUp, DollarSign, HeartHandshake, Sparkles } from "lucide-react";

const TEAM = "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?crop=entropy&cs=srgb&fm=jpg&q=85";

const offers = [
  { Icon: MapPin, title: "Remote Opportunities", copy: "Work from anywhere — sell, mentor, lead." },
  { Icon: CalendarClock, title: "Flexible Schedule", copy: "Build a business around your life, not the other way around." },
  { Icon: Users, title: "Mentorship & Training", copy: "1:1 coaching, daily training calls, proven playbook." },
  { Icon: GraduationCap, title: "Leadership Development", copy: "Become the leader other agents follow." },
  { Icon: TrendingUp, title: "Growth Opportunities", copy: "Clear paths to agency ownership and override income." },
  { Icon: DollarSign, title: "Competitive Compensation", copy: "Commission-based with bonuses, contests, and equity." },
  { Icon: HeartHandshake, title: "Supportive Team Culture", copy: "Faith, family, and finance — in that order." },
];

const traits = ["Coachable", "Driven", "Positive attitude", "Leadership-minded", "Passion for helping others"];

export default function Careers() {
  return (
    <div data-testid="careers-page">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={TEAM} alt="Team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-[#0D0D0D]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(212,175,55,0.12),_transparent_60%)]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-40 pb-24 w-full">
          <div className="overline mb-8 flex items-center gap-4">
            <span className="w-12 h-px bg-[#D4AF37]" /> Careers · Texas & Remote
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[7.5rem] tracking-tighter leading-[0.95] text-white max-w-5xl">
            Build a Career with <span className="italic text-[#D4AF37]">Purpose.</span>
          </h1>
          <p className="mt-10 text-white/75 max-w-2xl text-lg leading-relaxed">
            Whether you're licensed or completely new to the industry, we provide the tools, training, and mentorship to help you succeed — and build something worth leaving behind.
          </p>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="relative py-28 md:py-36 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-5">— What We Offer</div>
              <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
                A platform engineered for <span className="italic text-[#D4AF37]">your growth.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map(({ Icon, title, copy }, i) => (
              <div key={title} data-testid={`offer-${i}`} className="card-luxury p-8 group">
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] group-hover:text-black transition-all">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl text-white mb-3">{title}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE LOOK FOR */}
      <section className="relative py-28 md:py-36 border-t border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,139,139,0.08),_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <Sparkles className="w-8 h-8 text-[#D4AF37] mb-6" strokeWidth={1.5} />
            <div className="overline mb-5">— Who We're Looking For</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
              Character over credentials. <span className="italic text-[#D4AF37]">Always.</span>
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              We can teach the business. We can't teach the heart. If these five traits describe you, we want to talk.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-px bg-white/5">
            {traits.map((t, i) => (
              <div key={t} data-testid={`trait-${i}`} className="bg-[#0D0D0D] flex items-center justify-between p-8 hover:bg-[#1A1A1A] transition-colors group">
                <div className="flex items-center gap-8">
                  <span className="font-serif text-3xl text-[#D4AF37]">0{i + 1}</span>
                  <span className="font-serif text-3xl md:text-4xl text-white">{t}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight tracking-tight mb-8">
            Ready to make an impact and <span className="italic text-[#D4AF37]">build a legacy?</span>
          </h2>
          <p className="text-white/65 max-w-xl mx-auto mb-10 leading-relaxed">
            Apply today. A team member will reach out within 24 hours to schedule a conversation.
          </p>
          <Link to="/contact?interest=career" data-testid="careers-cta-apply" className="btn-primary">
            Apply Today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
